import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { getUserById } from '../utils/storage';
import '../styles/ReportPage.css';

const ReportPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = getUserById(userId);
    setUser(userData);
  }, [userId]);

  const handleViewHistory = () => {
    navigate(`/history/${userId}`);
  };

  if (!user) {
    return <div>加载中...</div>;
  }

  const latestReport = user.reports[0];

  return (
    <div className="page report-page">
      <Header title="检查报告" showBack />

      <div className="report-container">
        <div className="report-content">
          {/* 报告头部 */}
          <div className="report-header">
            <h2>{user.name}</h2>
            <div className="report-date">
              {latestReport?.fullDate || '2025-10-23'} 视力检测结果
            </div>
          </div>

          {/* 基础信息 */}
          <div className="basic-info">
            <div className="info-row">
              <span className="info-label">姓名：</span>
              <span className="info-value">{user.name}</span>
            </div>
            <div className="info-row">
              <span className="info-label">年龄：</span>
              <span className="info-value">7</span>
            </div>
            <div className="info-row">
              <span className="info-label">性别：</span>
              <span className="info-value">{user.gender === 'female' ? '女' : '男'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">病历号：</span>
              <span className="info-value">202508091</span>
            </div>
            <div className="info-row">
              <span className="info-label">报告时间：</span>
              <span className="info-value">{latestReport?.fullDate || '2025-10-23'}</span>
            </div>
          </div>

          {/* 眼部示意图 */}
          <div className="eye-section">
            <div className="eye-column">
              <div className="eye-label">OD</div>
              <div className="eye-icon">
                <svg viewBox="0 0 80 80">
                  <ellipse cx="40" cy="40" rx="35" ry="25" fill="#f8f9fa" stroke="#333" strokeWidth="2" />
                  <circle cx="40" cy="40" r="15" fill="#8b4513" />
                  <circle cx="40" cy="40" r="8" fill="#000" />
                  <circle cx="36" cy="36" r="3" fill="white" />
                </svg>
              </div>
            </div>
            <div className="eye-column">
              <div className="eye-label">OS</div>
              <div className="eye-icon">
                <svg viewBox="0 0 80 80">
                  <ellipse cx="40" cy="40" rx="35" ry="25" fill="#f8f9fa" stroke="#333" strokeWidth="2" />
                  <circle cx="40" cy="40" r="15" fill="#8b4513" />
                  <circle cx="40" cy="40" r="8" fill="#000" />
                  <circle cx="36" cy="36" r="3" fill="white" />
                </svg>
              </div>
            </div>
          </div>

          {/* 视力信息 */}
          <div className="section-title">视力信息</div>
          <DataTable
            rows={[
              { label: '裸眼视力', od: '4.0', os: '4.0' },
              { label: '戴镜视力', od: '5.10', os: '5.10' }
            ]}
          />

          {/* 屈光信息 */}
          <div className="section-title">屈光信息</div>
          <DataTable
            rows={[
              { label: '球镜 S(D)', od: '-7.75 (近视775度)', os: '-8.00 (近视800度)', highlight: true },
              { label: '柱镜 C(D)', od: '-1.50 (散光150度)', os: '-0.00', highlight: true },
              { label: '轴位 A(°)', od: '113', os: '0' },
              { label: '等效球镜 SE(D)', od: '-0.50', os: '-0.50' }
            ]}
          />

          {/* 查片仪信息 */}
          <div className="section-title">查片仪信息</div>
          <DataTable
            rows={[
              { label: '球镜 S(D)', od: '-5.50', os: '-4.00' },
              { label: '柱镜 C(D)', od: '-0.50', os: '-0.50' },
              { label: '轴位 A(°)', od: '100', os: '20' },
              { label: '等效球镜 SE(D)', od: '-0.50', os: '-0.50' }
            ]}
          />

          {/* 眼压信息 */}
          <div className="section-title">眼压信息</div>
          <DataTable
            rows={[
              { label: 'IOP(mmHg)', od: '16', os: '13' }
            ]}
          />

          {/* 生物测量信息 */}
          <div className="section-title">生物测量信息</div>
          <DataTable
            rows={[
              { label: '眼轴长 AL(mm)', od: '24.58', os: '24.18' },
              { label: '平坦 K1(D)', od: '43.75', os: '44.25' },
              { label: '陡峭 K2(D)', od: '45.25', os: '45.25' },
              { label: '角膜散光 AST(D)', od: '1.50', os: '1.00' },
              { label: '曲率均值 AVG K(D)', od: '44.50', os: '44.75' },
              { label: '瞳孔直径 PS(mm)', od: '6.50', os: '5.48' },
              { label: '白到白 WTW(mm)', od: '11.69', os: '11.49' },
              { label: '角膜厚度 CCT(mm)', od: '0.547', os: '0.553' },
              { label: '前房深度 AD(mm)', od: '3.00', os: '2.97' },
              { label: '晶体厚度 LT(mm)', od: '3.59', os: '3.78' },
              { label: '玻璃体厚度 VT(mm)', od: '17.44', os: '16.89' },
              { label: '瞳距 PD(mm)', od: '65', os: '65' },
              { label: '轴率比 AL/CR', od: '3.24', os: '3.21' }
            ]}
          />

          {/* 眼底影像 */}
          <div className="section-title">眼底影像</div>
          <div className="fundus-images">
            <FundusImage label="OD 眼底" />
            <FundusImage label="OS 眼底" />
          </div>

          {/* 眼底影像发现 */}
          <div className="section-title">眼底影像发现</div>
          <div className="diagnosis-result">
            <p><strong>OD:</strong> 视盘形态正常，视网膜血管走形正常，黄斑区域未见明显异常。</p>
            <p style={{ marginTop: '10px' }}><strong>OS:</strong> 视盘形态正常，视网膜血管走形正常，黄斑区域未见明显异常。</p>
          </div>

          {/* 眼底检查评估结果 */}
          <div className="section-title">眼底检查评估结果</div>
          <div className="diagnosis-result">
            <p><strong>OD:</strong> 眼底影像可见范围内未见明显异常。</p>
            <p style={{ marginTop: '10px' }}><strong>OS:</strong> 眼底影像可见范围内未见明显异常。</p>
          </div>

          {/* OCT影像 */}
          <div className="section-title">OCT影像</div>
          <div className="oct-images">
            <OctImageGroup label="OD" />
            <OctImageGroup label="OS" />
          </div>

          {/* 初检结果 */}
          <div className="section-title">初检结果</div>
          <div className="diagnosis-result">
            <p>
              <strong>右眼：</strong>
              疑似近视和散光，建议家长带孩子及时到正规眼科医疗机构就诊，做到早发现早干预，遵从医嘱进行科学的干预和视力矫正，注意科学用眼习惯，加强户外活动。
            </p>
            <p style={{ marginTop: '10px' }}>
              <strong>左眼：</strong>
              疑似近视，建议家长带孩子及时到正规眼科医疗机构检查，做到早发现早干预，遵从医嘱进行科学的干预和视力矫正，注意科学用眼习惯，加强户外活动。
            </p>
          </div>

          {/* 综合建议 */}
          <div className="section-title">综合建议</div>
          <div className="suggestion-box">
            <p className="suggestion-text">
              本次检查未发现眼底病变，日常请保持良好的用眼习惯，注意视力变化，如有视力下降等眼部不适，请及时到眼科检查，每年定期复查眼底。
            </p>
          </div>

          {/* 近视标准说明 */}
          <div className="standard-box">
            <h4>近视标准</h4>
            <p>一、裸眼视力＜5.0，且等效球镜＜-0.5D</p>
          </div>

          {/* 免责声明 */}
          <p className="disclaimer">
            注：本报告仅供筛查参考，不作为临床诊断依据。
          </p>
        </div>

        <div className="history-btn-container">
          <button className="btn-history" onClick={handleViewHistory}>
            查看历史记录
          </button>
        </div>
      </div>
    </div>
  );
};

// 数据表格组件
const DataTable = ({ rows }) => (
  <div className="data-table">
    {rows.map((row, index) => (
      <div key={index} className="data-row">
        <span className="data-label">{row.label}</span>
        <span className={`data-value ${row.highlight ? 'highlight' : ''}`}>
          {row.od}
        </span>
        <span className={`data-value ${row.highlight ? 'highlight' : ''}`}>
          {row.os}
        </span>
      </div>
    ))}
  </div>
);

// 眼底图像组件
const FundusImage = ({ label }) => (
  <div className="fundus-image">
    <span>{label}</span>
  </div>
);

// OCT图像组件
const OctImageGroup = ({ label }) => (
  <div className="oct-image-group">
    <div className="oct-image">
      <span>{label} OCT</span>
    </div>
    <div className="oct-chart">
      <span>GCC厚度图</span>
    </div>
  </div>
);

export default ReportPage;
